import {
  Box,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
  alpha,
  useTheme
} from '@mui/material'
import {
  EventCoverButton,
  EventCoverButtonRemove,
  PhotoIcon
} from 'assets/icons'
import IconButton from 'components/buttons/IconButton'
import { useConnection } from 'components/providers/ConnectionProvider'
import { IEventDetails } from 'hooks/useEventDetails'
import { t } from 'i18next'
import { useEffect, useRef, useState } from 'react'
import Logger from 'services/Logger'
import { EventNote } from 'types/api/changes'
import { useFilePicker } from 'use-file-picker'
import { compressBase64 } from 'utils/file'
import { capitalizeFirstChar } from 'utils/strings'
import { getCoverImageNote } from './EventCoverImage'
import GroupcalTooltip from 'components/tooltips/GroupcalTooltip'
import i18n from 'i18n'

interface GalleryButtonProps {
  eventHook: IEventDetails
}

export default function GalleryButton({ eventHook }: GalleryButtonProps) {
  const muiTheme = useTheme()
  const connection = useConnection()
  const menuAnchorRef = useRef<HTMLDivElement | null>(null)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const { openFilePicker, filesContent, clear } = useFilePicker({
    readAs: 'DataURL',
    accept: 'image/*',
    multiple: false
  })

  const coverImageNote = getCoverImageNote(eventHook.getValues('Notes'))
  const hasCoverImage = !!coverImageNote

  Logger.blue('GalleryButton', 'Rendering GalleryButton', eventHook.getValues())

  const handleMenuOpen = () => {
    if (menuAnchorRef.current) {
      setAnchorEl(menuAnchorRef.current)
    }
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleUploadClick = () => {
    handleMenuClose()
    openFilePicker()
  }

  const handleRemoveClick = () => {
    const currentNotes = eventHook.notes ?? []
    const filteredNotes = currentNotes.filter((n) => n.NoteType !== '1')
    eventHook.setNotes(filteredNotes)
    handleMenuClose()
  }

  useEffect(() => {
    Logger.debug('GalleryButton', 'filesContent changed', filesContent)
    if (filesContent.length > 0 && connection.isConnectionAvailable()) {
      compressBase64(filesContent[0].content)
        .then((data) => {
          const currentNotes =
            typeof eventHook.getValues('Notes') === 'string'
              ? []
              : (eventHook.getValues('Notes') ?? [])

          // Remove existing cover image note if any
          const filteredNotes = currentNotes.filter((n) => n.NoteType !== '1')

          const coverNote: EventNote = {
            FilePath: 'null',
            Note: capitalizeFirstChar(t('photo')),
            NoteID: Date.now(),
            Status: 1,
            FileBytes: data,
            NoteType: '1'
          }

          eventHook.setNotes([coverNote, ...filteredNotes])
          // Clear the file picker state after processing
          clear()
        })
        .catch((e) => {
          Logger.red('Error while compressing cover image', e, 'GalleryButton')
          // Clear even on error so user can try again
          clear()
        })
    }
  }, [filesContent])

  return (
    <Box sx={{ alignItems: 'end' }}>
      <GroupcalTooltip title={t('eventCoverImage')}>
        <Box
          ref={menuAnchorRef}
          sx={{ display: 'inline-flex', alignItems: 'end' }}
        >
          <IconButton onClick={handleMenuOpen} size="md" stopPropagationOnClick>
            <EventCoverButton
              style={{
                color: hasCoverImage
                  ? 'white'
                  : muiTheme.palette.input.placeholder
              }}
            />
          </IconButton>
        </Box>
      </GroupcalTooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        MenuListProps={{
          sx: {
            padding: '16px 0px 8px 0px'
          }
        }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              minWidth: 260,
              borderRadius: '12px',
              boxShadow: '0px 8px 24px rgba(0,0,0,0.14)',
              backgroundColor: 'white'
            }
          }
        }}
      >
        <Box
          display={'flex'}
          flexDirection={'row'}
          alignItems={'center'}
          mb={2}
        >
          <Typography
            ml={i18n.dir() === 'ltr' ? 4 : undefined}
            mr={i18n.dir() === 'rtl' ? 4 : undefined}
            width={'fit-content'}
            noWrap
            overflow={'visible'}
            variant="subtitle2"
            fontWeight={700}
            textTransform={'uppercase'}
            fontSize={'11px'}
          >
            {t('eventCoverImage')}
          </Typography>
        </Box>

        {!hasCoverImage && (
          <MenuItem onClick={handleUploadClick}>
            <ListItemIcon
              sx={{ height: 16, width: 16, minWidth: '24px !important' }}
            >
              <EventCoverButton
                style={{
                  color: alpha(muiTheme.palette.text.primary, 0.8),
                  width: 16,
                  height: 16
                }}
              />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ fontSize: '14px' }}>
              {t('uploadFromYourComputer')}
            </ListItemText>
          </MenuItem>
        )}

        {hasCoverImage && (
          <MenuItem onClick={handleRemoveClick}>
            <ListItemIcon
              sx={{ height: 16, width: 16, minWidth: '24px !important' }}
            >
              <EventCoverButtonRemove
                style={{
                  color: muiTheme.palette.input.placeholder,
                  width: 16,
                  height: 16
                }}
              />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ fontSize: '14px' }}>
              {t('removeCoverImage')}
            </ListItemText>
          </MenuItem>
        )}
      </Menu>
    </Box>
  )
}
